import type { ReelOutline } from './types';

/**
 * Type guard: checks that the parsed value has the expected ReelOutline shape.
 */
export function isReelOutline(
  value: unknown,
): value is ReelOutline {
  if (!value || typeof value !== 'object') return false;
  const obj: Record<string, unknown> = value as never;
  if (typeof obj.title !== 'string' || !Array.isArray(obj.chapters)) return false;

  // Validate chapter and beat shapes
  for (const ch of obj.chapters as unknown[]) {
    if (!ch || typeof ch !== 'object') return false;
    const chapter = ch as Record<string, unknown>;
    if (typeof chapter.title !== 'string' || !Array.isArray(chapter.beats)) return false;

    for (const b of chapter.beats as unknown[]) {
      if (!b || typeof b !== 'object') return false;
      const beat = b as Record<string, unknown>;
      if (typeof beat.description !== 'string' || typeof beat.estimatedDuration !== 'number') return false;
    }
  }

  return true;
}

/**
 * Extracts JSON from a raw AI response string.
 * Tries direct parse first, then looks for markdown fenced JSON blocks.
 */
export function parseOutlineFromResponse(raw: string): ReelOutline | null {
  // Try direct parse
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isReelOutline(parsed)) {
      return parsed;
    }
  } catch {
    // not raw JSON, try fenced
  }

  // Try extracting from markdown fences
  const fenceMatch = raw.match(/```(?:json)?\s*\n?([\s\S]*?)```/);
  if (fenceMatch) {
    try {
      const parsed: unknown = JSON.parse(fenceMatch[1]);
      if (isReelOutline(parsed)) {
        return parsed;
      }
    } catch {
      // parse failed
    }
  }

  return null;
}

/**
 * Try to parse a partial JSON stream into a usable outline.
 * Attempts to repair incomplete JSON by closing open arrays/objects.
 */
export function tryParsePartialOutline(raw: string): ReelOutline | null {
  if (!raw) return null;

  // Try direct parse first
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.title && Array.isArray(parsed.chapters)) return parsed;
  } catch {
    // incomplete, try repair
  }

  // Try extracting from markdown fences
  const fenceMatch = raw.match(/```(?:json)?\s*\n?([\s\S]*)/);
  const jsonStr = fenceMatch ? fenceMatch[1] : raw;

  // Attempt to repair incomplete JSON
  let repaired = jsonStr.trim();

  // Remove any trailing incomplete key-value pair (e.g., `"order": ` or `"title": "partial`)
  // by truncating back to the last complete value
  repaired = repaired.replace(/,\s*"[^"]*"\s*:\s*$/, '');           // trailing `,"key":`
  repaired = repaired.replace(/,\s*"[^"]*"\s*:\s*"[^"]*$/, '');     // trailing `,"key": "partial`
  repaired = repaired.replace(/,\s*"[^"]*"\s*:\s*\d+$/, '');        // trailing `,"key": 12`
  repaired = repaired.replace(/,\s*$/, '');                         // trailing comma

  // Close any unclosed strings
  const quoteCount = (repaired.match(/"/g) || []).length;
  if (quoteCount % 2 !== 0) repaired += '"';

  // Count unclosed brackets and close them
  let openBraces = 0;
  let openBrackets = 0;
  for (const ch of repaired) {
    if (ch === '{') openBraces++;
    if (ch === '}') openBraces--;
    if (ch === '[') openBrackets++;
    if (ch === ']') openBrackets--;
  }
  for (let i = 0; i < openBrackets; i++) repaired += ']';
  for (let i = 0; i < openBraces; i++) repaired += '}';

  try {
    const parsed = JSON.parse(repaired);
    if (parsed?.title && Array.isArray(parsed.chapters)) return parsed;
  } catch {
    // Try one more thing: truncate to last complete object/array boundary
    const lastGoodBracket = Math.max(repaired.lastIndexOf('}'), repaired.lastIndexOf(']'));
    if (lastGoodBracket > 0) {
      let truncated = repaired.substring(0, lastGoodBracket + 1);
      // Re-close
      let ob = 0, ab = 0;
      for (const ch of truncated) {
        if (ch === '{') ob++;
        if (ch === '}') ob--;
        if (ch === '[') ab++;
        if (ch === ']') ab--;
      }
      for (let i = 0; i < ab; i++) truncated += ']';
      for (let i = 0; i < ob; i++) truncated += '}';
      try {
        const parsed = JSON.parse(truncated);
        if (parsed?.title && Array.isArray(parsed.chapters)) return parsed;
      } catch {
        // give up
      }
    }
  }

  return null;
}
